/* *
 * Quote Calculator Modal
 * =============================
 * Scientific-style price calculator using basket rules and service catalog constants.
 * Mirrors the pricing logic from unified-basket.js and academic-services.js.
 *
 * Features:
 * - Calculator keypad (0-9, backspace, clear) + laptop keyboard support
 * - Function buttons: service category, education level, tier, complexity, delivery speed
 * - Live price calculation
 * - Keyboard-fully-operable
 *
 * @module AscendDraft-Website/js/components/quote-calculator-modal
 * @version 1.0.0 */

"use strict";

// ─────────────────────────────────────────────────────────────────────────────
// PRICING CONSTANTS (mirrors unified-basket.js + academic-services.js)
// ─────────────────────────────────────────────────────────────────────────────

const SERVICE_CATEGORIES = {
  academic:   { label: "Academic",   icon: "📚", base: { min: 3,  max: 14 } },
  career:     { label: "Career",     icon: "💼", base: { min: 3,  max: 7  } },
  design:     { label: "Design",     icon: "🎨", base: { min: 5,  max: 14 } },
  digital:    { label: "Digital",    icon: "💻", base: { min: 7,  max: 21 } },
  tutoring:   { label: "Tutoring",   icon: "🎓", base: { min: 1,  max: 5  } },
  automation: { label: "Automation", icon: "⚙️", base: { min: 3,  max: 14 } },
};

// Sub-services per category (used to populate the Service Type dropdown)
const SUB_SERVICES = {
  academic: [
    { id: "essay", label: "Essay Writing" },
    { id: "dissertation", label: "Dissertation / Thesis" },
    { id: "research_paper", label: "Research Paper" },
    { id: "literature_review", label: "Literature Review" },
    { id: "case_study", label: "Case Study" },
    { id: "lab_report", label: "Lab Report" },
    { id: "term_paper", label: "Term Paper" },
    { id: "research_proposal", label: "Research Proposal" },
    { id: "annotated_bib", label: "Annotated Bibliography" },
    { id: "proofreading_edit", label: "Editing & Proofreading" },
  ],
  career: [
    { id: "resume", label: "Resume Writing" },
    { id: "cover_letter", label: "Cover Letter" },
    { id: "cv", label: "CV Writing" },
    { id: "linkedin", label: "LinkedIn Optimisation" },
    { id: "bio", label: "Professional Bio" },
    { id: "interview_prep", label: "Interview Preparation" },
    { id: "personal_statement", label: "Personal Statement" },
  ],
  design: [
    { id: "logo", label: "Logo Design" },
    { id: "brand", label: "Brand Identity" },
    { id: "presentation", label: "Presentation / Slides" },
    { id: "pitch_deck", label: "Pitch Deck" },
    { id: "brochure", label: "Brochure / Flyer" },
    { id: "social_graphics", label: "Social Media Graphics" },
    { id: "infographic", label: "Infographic" },
  ],
  digital: [
    { id: "website", label: "Website Design" },
    { id: "seo", label: "SEO Optimisation" },
    { id: "social_media", label: "Social Media Content" },
    { id: "email_marketing", label: "Email Marketing" },
    { id: "app_design", label: "App / UI Design" },
    { id: "copywriting", label: "Web Copywriting" },
  ],
  tutoring: [
    { id: "one_on_one", label: "1-on-1 Tutoring Session" },
    { id: "exam_prep", label: "Exam Preparation" },
    { id: "concept_review", label: "Concept Review" },
    { id: "homework_help", label: "Homework Help" },
    { id: "group_session", label: "Group Session" },
    { id: "essay_coaching", label: "Essay / Writing Coaching" },
  ],
  automation: [
    { id: "workflow", label: "Workflow Automation" },
    { id: "data_entry", label: "Data Entry Automation" },
    { id: "report_gen", label: "Report Generation" },
    { id: "email_automation", label: "Email Automation" },
    { id: "web_scraping", label: "Web Scraping" },
    { id: "task_scheduler", label: "Task Scheduling" },
  ],
};

// Academic tier pricing per word by education level
const ACADEMIC_PRICING = {
  proofreading: { undergraduate: 0.035, masters: 0.045, phd: 0.055 },
  draft_refining: { undergraduate: 0.05, masters: 0.065, phd: 0.08 },
  model_paper: { undergraduate: 0.08, masters: 0.1, phd: 0.13 },
};

// Academic tier labels
const ACADEMIC_TIERS = {
  proofreading: { label: "Proofreading", icon: "🔍" },
  draft_refining: { label: "Draft Refining", icon: "✏️" },
  model_paper: { label: "Model Paper", icon: "📄" },
};

// Education levels
const EDUCATION_LEVELS = {
  undergraduate: { label: "Undergraduate", icon: "🎓", multiplier: 1.0 },
  masters: { label: "Master's", icon: "📖", multiplier: 1.0 / 0.8 },
  phd: { label: "PhD / Doctoral", icon: "🏛️", multiplier: 1.0 / 0.615 },
};

// Non-academic base prices — flat-rate only (word count does NOT affect price for these)
const NON_ACADEMIC_BASE = {
  career:     { wordRate: 0, basePrice: 25, label: "Career Services"     },
  design:     { wordRate: 0, basePrice: 45, label: "Design Services"     },
  digital:    { wordRate: 0, basePrice: 60, label: "Digital Services"    },
  tutoring:   { wordRate: 0, basePrice: 30, label: "Tutoring Services"   },
  automation: { wordRate: 0, basePrice: 50, label: "Automation Services" },
};

// Complexity multipliers (from COMPLEXITY_LEVELS in unified-basket.js)
const COMPLEXITY_LEVELS = {
  basic: {
    label: "Basic",
    icon: "📋",
    priceMultiplier: 1.0,
    timeMultiplier: 0.8,
  },
  standard: {
    label: "Standard",
    icon: "📁",
    priceMultiplier: 1.0,
    timeMultiplier: 1.0,
  },
  intermediate: {
    label: "Intermediate",
    icon: "📊",
    priceMultiplier: 1.25,
    timeMultiplier: 1.2,
  },
  advanced: {
    label: "Advanced",
    icon: "🎯",
    priceMultiplier: 1.5,
    timeMultiplier: 1.5,
  },
  expert: {
    label: "Expert",
    icon: "🚀",
    priceMultiplier: 2.0,
    timeMultiplier: 2.0,
  },
};

// Rush delivery tiers (from RUSH_DELIVERY_TIERS in unified-basket.js)
const RUSH_TIERS = {
  standard: { label: "Standard", icon: "🕐", multiplier: 1.0 },
  express: { label: "Express", icon: "⚡", multiplier: 1.1 },
  rush: { label: "Rush", icon: "🔥", multiplier: 1.15 },
  emergency: { label: "Emergency", icon: "🆘", multiplier: 1.25 },
};

// Word count delivery time thresholds (from WORD_COUNT_THRESHOLDS in unified-basket.js)
const WORD_COUNT_THRESHOLDS = [
  { max: 500, deliveryMultiplier: 0.8, label: "Short" },
  { max: 1000, deliveryMultiplier: 0.9, label: "Brief" },
  { max: 2500, deliveryMultiplier: 1.0, label: "Standard" },
  { max: 5000, deliveryMultiplier: 1.2, label: "Medium" },
  { max: 10000, deliveryMultiplier: 1.5, label: "Long" },
  { max: 20000, deliveryMultiplier: 2.0, label: "Extended" },
  { max: Infinity, deliveryMultiplier: 2.5, label: "Comprehensive" },
];

// Package multipliers (academic bundles)
const PACKAGE_MULTIPLIERS = {
  a: { label: "Option A", multiplier: 1.0, desc: "Core service only" },
  b: { label: "Option B", multiplier: 1.15, desc: "Enhanced service (+15%)" },
  complete: {
    label: "Complete Bundle",
    multiplier: (1.0 + 1.15) * 0.95,
    desc: "A + B with 5% bundle discount",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVE CATEGORIES (pruned by API on open; full list as fallback)
// ─────────────────────────────────────────────────────────────────────────────

let _filteredCategories = { ...SERVICE_CATEGORIES };
let _apiCategoriesFetched = false;

// Returns the absolute URL to a service page on this static site.
// Uses window.location.origin so it works on localhost, staging, and production.
function getServicePageUrl(category) {
  return `${window.location.origin}/pages/services/${category}/`;
}

async function fetchActiveCategories() {
  if (_apiCategoriesFetched) return;
  _apiCategoriesFetched = true;
  try {
    const apiBase = window.ASCEND_API_URL || "https://api.ascenddraft.com/api";
    const res = await fetch(`${apiBase}/public/services`, {
      signal: AbortSignal.timeout(4000),
    });
    if (!res.ok) return;
    const json = await res.json();
    const services = Array.isArray(json.services) ? json.services : [];
    const activeKeys = new Set(services.map((s) => s.category).filter(Boolean));
    // Keep only categories that are both supported by the calculator AND active in DB
    _filteredCategories = Object.fromEntries(
      Object.entries(SERVICE_CATEGORIES).filter(([k]) => activeKeys.has(k)),
    );
    // Ensure at least one category is active (fall back to full list if API returns nothing useful)
    if (Object.keys(_filteredCategories).length === 0) {
      _filteredCategories = { ...SERVICE_CATEGORIES };
    }
    // If current state category was deactivated, switch to first active one
    if (!_filteredCategories[state.category]) {
      state.category = Object.keys(_filteredCategories)[0];
    }
  } catch {
    // Fail open — use full hardcoded list
    _filteredCategories = { ...SERVICE_CATEGORIES };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CALCULATOR STATE
// ─────────────────────────────────────────────────────────────────────────────

let state = {
  category: "academic",
  subService: "essay",          // selected sub-service within the category
  academicTier: "model_paper",
  educationLevel: "undergraduate",
  complexity: "standard",
  delivery: "standard",
  wordCount: 1000,
  includeProofreading: false,
  includeEditing: false,
  package: "a",
  // Internal display state for numpad input
  _inputBuffer: "1000",
  _inputFocused: false,
};

// ─────────────────────────────────────────────────────────────────────────────
// PRICING CALCULATION ENGINE
// ─────────────────────────────────────────────────────────────────────────────

function getWordCountDeliveryFactor(words) {
  for (const t of WORD_COUNT_THRESHOLDS) {
    if (words <= t.max) return t.deliveryMultiplier;
  }
  return 2.5;
}

function calculatePrice() {
  const {
    category,
    academicTier,
    educationLevel,
    complexity,
    delivery,
    wordCount,
    includeProofreading,
    includeEditing,
    package: pkg,
  } = state;
  const complexityMult = COMPLEXITY_LEVELS[complexity]?.priceMultiplier ?? 1.0;
  const rushMult = RUSH_TIERS[delivery]?.multiplier ?? 1.0;
  const words = Math.max(0, wordCount || 0);

  let baseAmount = 0;

  if (category === "academic") {
    const tierRates =
      ACADEMIC_PRICING[academicTier] || ACADEMIC_PRICING.model_paper;
    const rate = tierRates[educationLevel] || tierRates.undergraduate;
    const minBase = rate * 250; // minimum ~250 words equivalent
    const wordBased = words * rate;
    baseAmount = Math.max(minBase, wordBased);

    // Apply package multiplier for academic bundle
    const pkgMult = PACKAGE_MULTIPLIERS[pkg]?.multiplier ?? 1.0;
    baseAmount *= pkgMult;
  } else {
    const cat = NON_ACADEMIC_BASE[category] || NON_ACADEMIC_BASE.career;
    if (cat.wordRate > 0 && words > 0) {
      const wordBased = words * cat.wordRate;
      baseAmount = Math.max(cat.basePrice, wordBased);
    } else {
      baseAmount = cat.basePrice;
    }
  }

  // Apply complexity
  baseAmount *= complexityMult;

  // Add-ons (academic only)
  let addOnAmount = 0;
  if (
    category === "academic" &&
    includeProofreading &&
    academicTier !== "proofreading"
  ) {
    const proofRate =
      ACADEMIC_PRICING.proofreading[educationLevel] ||
      ACADEMIC_PRICING.proofreading.undergraduate;
    addOnAmount += Math.max(proofRate * 250, words * proofRate);
  }
  if (
    category === "academic" &&
    includeEditing &&
    academicTier !== "draft_refining"
  ) {
    const editRate =
      ACADEMIC_PRICING.draft_refining[educationLevel] ||
      ACADEMIC_PRICING.draft_refining.undergraduate;
    addOnAmount += Math.max(editRate * 250, words * editRate);
  }

  const subtotal = baseAmount + addOnAmount;
  const total = subtotal * rushMult;

  // Delivery estimate
  const baseDelivery = SERVICE_CATEGORIES[category]?.base ?? {
    min: 3,
    max: 14,
  };
  const wdFactor = getWordCountDeliveryFactor(words);
  const timeMult =
    (COMPLEXITY_LEVELS[complexity]?.timeMultiplier ?? 1.0) *
    wdFactor *
    rushMult;
  // Rush reduces delivery time (smaller multiplier = faster delivery)
  const adjustedMin = Math.max(
    1,
    Math.round((baseDelivery.min * timeMult) / rushMult),
  );
  const adjustedMax = Math.max(
    adjustedMin + 1,
    Math.round((baseDelivery.max * timeMult) / rushMult),
  );

  return {
    base: baseAmount,
    addOn: addOnAmount,
    subtotal,
    total,
    delivery:
      rushMult <= 1.0
        ? `${adjustedMin}–${adjustedMax} days`
        : `${Math.max(1, Math.round(adjustedMin * 0.7))}–${Math.max(2, Math.round(adjustedMax * 0.7))} days`,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────────────────

const MODAL_STYLES = `
/* ── QCM Theme tokens — inherit from platform theme-engine CSS vars ──────── */
#qcm-overlay,#qcm-modal,#qcm-modal * {
  --qcm-bg:       var(--color-bg-base,     #0f1219);
  --qcm-surface:  var(--color-bg-card,     #1a1f2e);
  --qcm-elevated: var(--color-bg-elevated, #141929);
  --qcm-border:   var(--divider,           #2d3548);
  --qcm-border2:  var(--divider,           #1e2536);
  --qcm-text:     var(--color-text-primary,   #e2e8f0);
  --qcm-muted:    var(--color-text-muted,     #4a5568);
  --qcm-sub:      var(--color-text-secondary, #94a3b8);
  --qcm-accent:   var(--color-primary,        #58a6ff);
}

#qcm-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0,0,0,0.75);
  backdrop-filter: blur(6px);
  animation: qcmFadeIn 0.2s ease;
}
@keyframes qcmFadeIn { from { opacity:0 } to { opacity:1 } }

#qcm-modal {
  position: relative;
  background: var(--qcm-bg);
  border: 1px solid var(--qcm-border);
  border-radius: 20px;
  width: 100%;
  max-width: 680px;
  max-height: 92vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(88,166,255,0.08);
  animation: qcmSlideIn 0.3s cubic-bezier(0.34,1.56,0.64,1);
}
@keyframes qcmSlideIn {
  from { opacity:0; transform: translateY(-24px) scale(0.95); }
  to   { opacity:1; transform: translateY(0)    scale(1); }
}

.qcm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--qcm-border2);
  background: linear-gradient(135deg, var(--qcm-bg) 0%, var(--qcm-elevated) 100%);
  border-radius: 20px 20px 0 0;
  flex-shrink: 0;
}
.qcm-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--qcm-text);
  margin: 0;
}
.qcm-title-icon { font-size: 1.3rem; }
.qcm-close {
  width: 34px; height: 34px;
  border-radius: 50%;
  border: 1px solid var(--qcm-border);
  background: var(--qcm-surface);
  color: var(--qcm-sub);
  font-size: 1.1rem;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.qcm-close:hover { background: var(--qcm-border); color: var(--qcm-text); }
.qcm-close:focus { outline: 2px solid var(--qcm-accent); outline-offset: 2px; }
.qcm-expand {
  width: 34px; height: 34px;
  border-radius: 50%;
  border: 1px solid var(--qcm-border);
  background: var(--qcm-surface);
  color: var(--qcm-sub);
  font-size: 1rem;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
  margin-right: 0.4rem;
}
.qcm-expand:hover { background: var(--qcm-border); color: var(--qcm-text); }
.qcm-expand:focus { outline: 2px solid var(--qcm-accent); outline-offset: 2px; }
#qcm-overlay.qcm--expanded #qcm-modal { max-width: 920px; }

.qcm-body {
  display: grid;
  grid-template-columns: 1fr 260px;
  gap: 0;
  flex: 1;
}

/* LEFT PANEL display + function keys */
.qcm-left {
  padding: 1.25rem;
  border-right: 1px solid var(--qcm-border2);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Display */
.qcm-display {
  background: var(--qcm-bg);
  border: 1px solid var(--qcm-border2);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.qcm-display-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--qcm-muted);
}
.qcm-display-price {
  font-size: 2.2rem;
  font-weight: 800;
  color: var(--qcm-accent);
  letter-spacing: -0.02em;
  line-height: 1;
}
.qcm-display-sub {
  display: flex;
  gap: 1.25rem;
  margin-top: 0.5rem;
}
.qcm-display-sub-item {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.qcm-display-sub-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--qcm-muted);
}
.qcm-display-sub-val {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--qcm-sub);
}
.qcm-display-delivery {
  font-size: 0.78rem;
  color: #48bb78;
  margin-top: 0.25rem;
  font-weight: 500;
}

/* Word count input row */
.qcm-wc-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.qcm-wc-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--qcm-muted);
  white-space: nowrap;
}
.qcm-wc-input-wrap {
  flex: 1;
  position: relative;
}
.qcm-wc-input {
  width: 100%;
  background: var(--qcm-surface);
  border: 1px solid var(--qcm-border);
  border-radius: 8px;
  color: var(--qcm-text);
  font-size: 1rem;
  font-weight: 600;
  padding: 0.55rem 2.5rem 0.55rem 0.75rem;
  text-align: right;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.qcm-wc-input:focus {
  outline: none;
  border-color: var(--qcm-accent);
  box-shadow: 0 0 0 3px rgba(88,166,255,0.15);
}
.qcm-wc-unit {
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.65rem;
  color: var(--qcm-muted);
  pointer-events: none;
}
/* Word count note for flat-rate services */
.qcm-wc-note {
  font-size: 0.68rem;
  color: var(--qcm-muted);
  margin: -0.5rem 0 0;
  padding: 0.3rem 0.5rem;
  background: rgba(237,137,54,0.08);
  border: 1px solid rgba(237,137,54,0.2);
  border-radius: 6px;
  line-height: 1.4;
  display: none;
}
.qcm-wc-note.visible { display: block; }

/* Sub-service dropdown */
.qcm-select {
  width: 100%;
  background: var(--qcm-surface);
  border: 1px solid var(--qcm-border);
  border-radius: 8px;
  color: var(--qcm-text);
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: border-color 0.15s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2394a3b8' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2rem;
}
.qcm-select:focus {
  outline: none;
  border-color: var(--qcm-accent);
  box-shadow: 0 0 0 3px rgba(88,166,255,0.15);
}
.qcm-select option { background: var(--qcm-surface); color: var(--qcm-text); }

/* Function button groups */
.qcm-group-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--qcm-muted);
  margin-bottom: 0.4rem;
}
.qcm-btn-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.qcm-fn-btn {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.7rem;
  border-radius: 8px;
  border: 1px solid var(--qcm-border);
  background: var(--qcm-surface);
  color: var(--qcm-sub);
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.qcm-fn-btn:hover {
  border-color: var(--qcm-sub);
  color: var(--qcm-text);
  background: var(--qcm-elevated);
}
.qcm-fn-btn:focus {
  outline: 2px solid var(--qcm-accent);
  outline-offset: 2px;
}
.qcm-fn-btn.active {
  border-color: var(--qcm-accent);
  background: rgba(88,166,255,0.12);
  color: var(--qcm-accent);
  font-weight: 600;
}
.qcm-fn-btn.active-green {
  border-color: #48bb78;
  background: rgba(72,187,120,0.12);
  color: #48bb78;
  font-weight: 600;
}
.qcm-fn-btn.active-orange {
  border-color: #ed8936;
  background: rgba(237,137,54,0.12);
  color: #ed8936;
  font-weight: 600;
}
.qcm-fn-btn.active-red {
  border-color: #fc8181;
  background: rgba(252,129,129,0.12);
  color: #fc8181;
  font-weight: 600;
}

.qcm-addons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.qcm-addon-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  border: 1px dashed var(--qcm-border);
  background: transparent;
  color: var(--qcm-muted);
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.15s;
}
.qcm-addon-btn:hover { border-color: var(--qcm-sub); color: var(--qcm-sub); }
.qcm-addon-btn.active {
  border: 1px solid #48bb78;
  background: rgba(72,187,120,0.1);
  color: #48bb78;
  font-weight: 600;
}
.qcm-addon-btn:focus { outline: 2px solid var(--qcm-accent); outline-offset: 2px; }
.qcm-addon-check { font-size: 0.85rem; }

/* RIGHT PANEL numpad */
.qcm-right {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.qcm-numpad-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--qcm-muted);
  text-align: center;
}
.qcm-numpad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  flex: 1;
}
.qcm-num-btn {
  aspect-ratio: 1;
  border-radius: 10px;
  border: 1px solid var(--qcm-border);
  background: var(--qcm-surface);
  color: var(--qcm-text);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
}
.qcm-num-btn:hover { background: var(--qcm-elevated); border-color: var(--qcm-sub); }
.qcm-num-btn:active { transform: scale(0.94); }
.qcm-num-btn:focus { outline: 2px solid var(--qcm-accent); outline-offset: 2px; }
.qcm-num-btn.zero { grid-column: span 2; aspect-ratio: auto; padding: 0.65rem; }
.qcm-num-btn.del {
  background: rgba(192,132,252,0.08);
  border-color: rgba(192,132,252,0.25);
  color: #c084fc;
  font-size: 0.9rem;
}
.qcm-num-btn.del:hover { background: rgba(192,132,252,0.18); }
.qcm-num-btn.clear {
  grid-column: span 3;
  aspect-ratio: auto;
  padding: 0.65rem;
  background: rgba(237,137,54,0.08);
  border-color: rgba(237,137,54,0.25);
  color: #ed8936;
  font-size: 0.82rem;
}
.qcm-num-btn.clear:hover { background: rgba(237,137,54,0.18); }

.qcm-quick-presets {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.qcm-preset-label {
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--qcm-muted);
  text-align: center;
}
.qcm-presets {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.4rem;
}
.qcm-preset-btn {
  padding: 0.45rem 0.25rem;
  border-radius: 8px;
  border: 1px solid var(--qcm-border);
  background: var(--qcm-elevated);
  color: var(--qcm-muted);
  font-size: 0.72rem;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s;
}
.qcm-preset-btn:hover { border-color: var(--qcm-accent); color: var(--qcm-sub); }
.qcm-preset-btn:focus { outline: 2px solid var(--qcm-accent); outline-offset: 2px; }

.qcm-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--qcm-border2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-shrink: 0;
  background: var(--qcm-bg);
  border-radius: 0 0 20px 20px;
}
.qcm-footer-note {
  font-size: 0.72rem;
  color: var(--qcm-muted);
  line-height: 1.4;
}
.qcm-cta-btn {
  padding: 0.65rem 1.5rem;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--qcm-accent) 0%, #3d8bcd 100%);
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}
.qcm-cta-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(88,166,255,0.35);
}
.qcm-cta-btn:focus { outline: 2px solid #fff; outline-offset: 2px; }

/* Responsive */
@media (max-width: 580px) {
  .qcm-body { grid-template-columns: 1fr; }
  .qcm-left { border-right: none; border-bottom: 1px solid var(--qcm-border2); }
  .qcm-right { padding: 1rem; }
  .qcm-numpad { max-width: 260px; margin: 0 auto; }
  .qcm-display-price { font-size: 1.8rem; }
}

/* Academic-only sections — fully hidden when category is not academic */
.qcm-academic-only { display: block; }
.qcm-academic-only.hidden { display: none; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// BUILD MODAL HTML
// ─────────────────────────────────────────────────────────────────────────────

function buildHTML() {
  const catBtns = Object.entries(_filteredCategories)
    .map(
      ([k, v]) =>
        `<button class="qcm-fn-btn${state.category === k ? " active" : ""}" data-qcm-cat="${k}" aria-pressed="${state.category === k}">${v.icon} ${v.label}</button>`,
    )
    .join("");

  const tierBtns = Object.entries(ACADEMIC_TIERS)
    .map(
      ([k, v]) =>
        `<button class="qcm-fn-btn${state.academicTier === k ? " active-green" : ""}" data-qcm-tier="${k}" aria-pressed="${state.academicTier === k}">${v.icon} ${v.label}</button>`,
    )
    .join("");

  const levelBtns = Object.entries(EDUCATION_LEVELS)
    .map(
      ([k, v]) =>
        `<button class="qcm-fn-btn${state.educationLevel === k ? " active" : ""}" data-qcm-level="${k}" aria-pressed="${state.educationLevel === k}">${v.icon} ${v.label}</button>`,
    )
    .join("");

  const complexityBtns = Object.entries(COMPLEXITY_LEVELS)
    .map(
      ([k, v]) =>
        `<button class="qcm-fn-btn${state.complexity === k ? " active-orange" : ""}" data-qcm-complexity="${k}" aria-pressed="${state.complexity === k}">${v.icon} ${v.label}</button>`,
    )
    .join("");

  const deliveryBtns = Object.entries(RUSH_TIERS)
    .map(
      ([k, v]) =>
        `<button class="qcm-fn-btn${state.delivery === k ? " active-red" : ""}" data-qcm-delivery="${k}" aria-pressed="${state.delivery === k}">${v.icon} ${v.label}</button>`,
    )
    .join("");

  const pkgBtns = Object.entries(PACKAGE_MULTIPLIERS)
    .map(
      ([k, v]) =>
        `<button class="qcm-fn-btn${state.package === k ? " active-green" : ""}" data-qcm-pkg="${k}" title="${v.desc}" aria-pressed="${state.package === k}">${v.label}</button>`,
    )
    .join("");

  const isAcademic = state.category === "academic";
  const price = calculatePrice();

  const numpad = [7, 8, 9, 4, 5, 6, 1, 2, 3]
    .map(
      (n) =>
        `<button class="qcm-num-btn" data-qcm-num="${n}" aria-label="${n}">${n}</button>`,
    )
    .join("");

  return `
<div id="qcm-overlay" role="dialog" aria-modal="true" aria-label="Quote Calculator">
  <div id="qcm-modal">
    <div class="qcm-header">
      <h2 class="qcm-title">
        <span class="qcm-title-icon">🧮</span>
        Quote Calculator
      </h2>
      <button class="qcm-expand" id="qcm-expand-btn" aria-label="Expand calculator" title="Expand">&#x26F6;</button>
      <button class="qcm-close" id="qcm-close-btn" aria-label="Close calculator">✕</button>
    </div>

    <div class="qcm-body">
      <!-- LEFT: Function keys + display -->
      <div class="qcm-left">

        <!-- DISPLAY -->
        <div class="qcm-display" aria-live="polite" aria-label="Price estimate">
          <span class="qcm-display-label">Estimated Quote</span>
          <span class="qcm-display-price" id="qcm-price">$${price.total.toFixed(2)}</span>
          <div class="qcm-display-sub">
            <div class="qcm-display-sub-item">
              <span class="qcm-display-sub-label">Base</span>
              <span class="qcm-display-sub-val" id="qcm-base">$${price.base.toFixed(2)}</span>
            </div>
            ${
              price.addOn > 0
                ? `<div class="qcm-display-sub-item">
              <span class="qcm-display-sub-label">Add-ons</span>
              <span class="qcm-display-sub-val" id="qcm-addon">+$${price.addOn.toFixed(2)}</span>
            </div>`
                : `<div class="qcm-display-sub-item">
              <span class="qcm-display-sub-label">Add-ons</span>
              <span class="qcm-display-sub-val" id="qcm-addon" style="color:#4a5568">—</span>
            </div>`
            }
            <div class="qcm-display-sub-item">
              <span class="qcm-display-sub-label">Delivery</span>
              <span class="qcm-display-delivery" id="qcm-delivery-est">~${price.delivery}</span>
            </div>
          </div>
        </div>

        <!-- Word count -->
        <div class="qcm-wc-row">
          <span class="qcm-wc-label">Word Count</span>
          <div class="qcm-wc-input-wrap">
            <input
              type="number"
              class="qcm-wc-input"
              id="qcm-wc-input"
              value="${state.wordCount}"
              min="0"
              max="100000"
              step="100"
              aria-label="Word count"
            />
            <span class="qcm-wc-unit">words</span>
          </div>
        </div>
        <p class="qcm-wc-note${isAcademic ? "" : " visible"}" id="qcm-wc-note">
          ℹ️ This is a flat-rate service, so word count doesn't affect the price estimate.
        </p>

        <!-- Service Category -->
        <div>
          <div class="qcm-group-label">Service Category</div>
          <div class="qcm-btn-row" role="group" aria-label="Service category">${catBtns}</div>
        </div>

        <!-- Service Type (sub-service dropdown — all categories) -->
        <div>
          <div class="qcm-group-label">Service Type</div>
          <select class="qcm-select" id="qcm-sub-select" aria-label="Service type">
            ${(SUB_SERVICES[state.category] || []).map(
              (s) =>
                `<option value="${s.id}"${state.subService === s.id ? " selected" : ""}>${s.label}</option>`,
            ).join("")}
          </select>
        </div>

        <!-- Academic Tier (academic only) -->
        <div class="qcm-academic-only${isAcademic ? "" : " hidden"}" id="qcm-tier-group">
          <div class="qcm-group-label">Service Tier</div>
          <div class="qcm-btn-row" role="group" aria-label="Academic tier">${tierBtns}</div>
        </div>

        <!-- Education Level (academic only) -->
        <div class="qcm-academic-only${isAcademic ? "" : " hidden"}" id="qcm-level-group">
          <div class="qcm-group-label">Education Level</div>
          <div class="qcm-btn-row" role="group" aria-label="Education level">${levelBtns}</div>
        </div>

        <!-- Academic Package (academic only) -->
        <div class="qcm-academic-only${isAcademic ? "" : " hidden"}" id="qcm-pkg-group">
          <div class="qcm-group-label">Package</div>
          <div class="qcm-btn-row" role="group" aria-label="Package">${pkgBtns}</div>
        </div>

        <!-- Complexity -->
        <div>
          <div class="qcm-group-label">Complexity</div>
          <div class="qcm-btn-row" role="group" aria-label="Complexity">${complexityBtns}</div>
        </div>

        <!-- Delivery Speed -->
        <div>
          <div class="qcm-group-label">Delivery Speed</div>
          <div class="qcm-btn-row" role="group" aria-label="Delivery speed">${deliveryBtns}</div>
        </div>

        <!-- Add-ons (academic only) -->
        <div class="qcm-academic-only${isAcademic ? "" : " hidden"}" id="qcm-addons-group">
          <div class="qcm-group-label">Add-ons</div>
          <div class="qcm-addons">
            <button class="qcm-addon-btn${state.includeProofreading && state.academicTier !== "proofreading" ? " active" : ""}" data-qcm-addon="proofreading" aria-pressed="${state.includeProofreading}">
              <span class="qcm-addon-check">${state.includeProofreading && state.academicTier !== "proofreading" ? "✓" : "+"}</span>
              🔍 Proofreading
            </button>
            <button class="qcm-addon-btn${state.includeEditing && state.academicTier !== "draft_refining" ? " active" : ""}" data-qcm-addon="editing" aria-pressed="${state.includeEditing}">
              <span class="qcm-addon-check">${state.includeEditing && state.academicTier !== "draft_refining" ? "✓" : "+"}</span>
              ✏️ Draft Editing
            </button>
          </div>
        </div>

      </div>

      <!-- RIGHT: Numpad -->
      <div class="qcm-right">
        <div class="qcm-numpad-label">Word Count Keypad</div>

        <div class="qcm-numpad" role="group" aria-label="Number pad">
          ${numpad}
          <button class="qcm-num-btn zero" data-qcm-num="0" aria-label="0">0</button>
          <button class="qcm-num-btn del" data-qcm-num="del" aria-label="Delete">⌫</button>
          <button class="qcm-num-btn clear" data-qcm-num="clear" aria-label="Clear">CLR</button>
        </div>

        <div class="qcm-quick-presets">
          <div class="qcm-preset-label">Quick Presets</div>
          <div class="qcm-presets">
            <button class="qcm-preset-btn" data-qcm-preset="500" aria-label="500 words">500 words</button>
            <button class="qcm-preset-btn" data-qcm-preset="1000" aria-label="1000 words">1,000 words</button>
            <button class="qcm-preset-btn" data-qcm-preset="2500" aria-label="2500 words">2,500 words</button>
            <button class="qcm-preset-btn" data-qcm-preset="5000" aria-label="5000 words">5,000 words</button>
            <button class="qcm-preset-btn" data-qcm-preset="10000" aria-label="10000 words">10,000 words</button>
            <button class="qcm-preset-btn" data-qcm-preset="20000" aria-label="20000 words">20,000 words</button>
          </div>
        </div>
      </div>
    </div>

    <div class="qcm-footer">
      <p class="qcm-footer-note">
        Estimates based on current pricing. Final quote confirmed after order details review.
        Member discounts applied at checkout.
      </p>
      <a href="#" class="qcm-cta-btn" id="qcm-order-btn">
        Start Order →
      </a>
    </div>
  </div>
</div>
`;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE DISPLAY (partial re-render — only price numbers and button states)
// ─────────────────────────────────────────────────────────────────────────────

function updateDisplay() {
  const price = calculatePrice();
  const el = (id) => document.getElementById(id);

  const priceEl = el("qcm-price");
  if (priceEl) priceEl.textContent = `$${price.total.toFixed(2)}`;

  const baseEl = el("qcm-base");
  if (baseEl) baseEl.textContent = `$${price.base.toFixed(2)}`;

  const addonEl = el("qcm-addon");
  if (addonEl) {
    if (price.addOn > 0) {
      addonEl.textContent = `+$${price.addOn.toFixed(2)}`;
      addonEl.style.color = "";
    } else {
      addonEl.textContent = "—";
      addonEl.style.color = "#4a5568";
    }
  }

  const delivEl = el("qcm-delivery-est");
  if (delivEl) delivEl.textContent = `~${price.delivery}`;
}

function updateButtonStates() {
  // Category buttons
  document.querySelectorAll("[data-qcm-cat]").forEach((btn) => {
    const active = btn.dataset.qcmCat === state.category;
    btn.classList.toggle("active", active);
    btn.setAttribute("aria-pressed", String(active));
  });

  // Tier buttons
  document.querySelectorAll("[data-qcm-tier]").forEach((btn) => {
    const active = btn.dataset.qcmTier === state.academicTier;
    btn.classList.toggle("active-green", active);
    btn.setAttribute("aria-pressed", String(active));
  });

  // Level buttons
  document.querySelectorAll("[data-qcm-level]").forEach((btn) => {
    const active = btn.dataset.qcmLevel === state.educationLevel;
    btn.classList.toggle("active", active);
    btn.setAttribute("aria-pressed", String(active));
  });

  // Complexity buttons
  document.querySelectorAll("[data-qcm-complexity]").forEach((btn) => {
    const active = btn.dataset.qcmComplexity === state.complexity;
    btn.classList.toggle("active-orange", active);
    btn.setAttribute("aria-pressed", String(active));
  });

  // Delivery buttons
  document.querySelectorAll("[data-qcm-delivery]").forEach((btn) => {
    const active = btn.dataset.qcmDelivery === state.delivery;
    btn.classList.toggle("active-red", active);
    btn.setAttribute("aria-pressed", String(active));
  });

  // Package buttons
  document.querySelectorAll("[data-qcm-pkg]").forEach((btn) => {
    const active = btn.dataset.qcmPkg === state.package;
    btn.classList.toggle("active-green", active);
    btn.setAttribute("aria-pressed", String(active));
  });

  // Academic-only groups visibility
  const isAcademic = state.category === "academic";
  document.querySelectorAll(".qcm-academic-only").forEach((el) => {
    el.classList.toggle("hidden", !isAcademic);
  });

  // Add-on buttons
  const proofAddon = document.querySelector("[data-qcm-addon='proofreading']");
  const editAddon = document.querySelector("[data-qcm-addon='editing']");
  if (proofAddon) {
    const active =
      state.includeProofreading && state.academicTier !== "proofreading";
    proofAddon.classList.toggle("active", active);
    proofAddon.setAttribute("aria-pressed", String(active));
    proofAddon.querySelector(".qcm-addon-check").textContent = active
      ? "✓"
      : "+";
  }
  if (editAddon) {
    const active =
      state.includeEditing && state.academicTier !== "draft_refining";
    editAddon.classList.toggle("active", active);
    editAddon.setAttribute("aria-pressed", String(active));
    editAddon.querySelector(".qcm-addon-check").textContent = active
      ? "✓"
      : "+";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// NUMPAD LOGIC
// ─────────────────────────────────────────────────────────────────────────────

function numpadPress(key) {
  const input = document.getElementById("qcm-wc-input");
  if (!input) return;

  if (key === "clear") {
    state._inputBuffer = "0";
  } else if (key === "del") {
    state._inputBuffer =
      state._inputBuffer.length > 1 ? state._inputBuffer.slice(0, -1) : "0";
  } else {
    if (state._inputBuffer === "0") {
      state._inputBuffer = String(key);
    } else {
      if (state._inputBuffer.length >= 6) return; // max 999999
      state._inputBuffer = state._inputBuffer + String(key);
    }
  }

  const val = Math.min(99999, parseInt(state._inputBuffer, 10) || 0);
  state.wordCount = val;
  input.value = val;
  updateDisplay();
}

function syncWordCount(val) {
  const n = Math.max(0, Math.min(99999, parseInt(val, 10) || 0));
  state.wordCount = n;
  state._inputBuffer = String(n);
  updateDisplay();
}

// ─────────────────────────────────────────────────────────────────────────────
// KEYBOARD HANDLER
// ─────────────────────────────────────────────────────────────────────────────

function handleKeydown(e) {
  // Don't intercept if user is typing in the word count text input
  if (e.target && e.target.id === "qcm-wc-input") return;

  if (e.key === "Escape") {
    closeModal();
    return;
  }

  if (e.key >= "0" && e.key <= "9") {
    e.preventDefault();
    numpadPress(parseInt(e.key, 10));
    return;
  }

  if (e.key === "Backspace" || e.key === "Delete") {
    e.preventDefault();
    numpadPress("del");
    return;
  }

  if (e.key === "c" || e.key === "C") {
    numpadPress("clear");
    return;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// OPEN / CLOSE
// ─────────────────────────────────────────────────────────────────────────────

let _keydownHandler = null;

async function openModal() {
  // Fetch active categories from DB before first render
  await fetchActiveCategories();

  // Remove any existing instance
  const existing = document.getElementById("qcm-overlay");
  if (existing) existing.remove();

  // Inject styles once
  if (!document.getElementById("qcm-styles")) {
    const style = document.createElement("style");
    style.id = "qcm-styles";
    style.textContent = MODAL_STYLES;
    document.head.appendChild(style);
  }

  // Reset state for fresh open
  state._inputBuffer = String(state.wordCount || 1000);

  // Render
  const wrapper = document.createElement("div");
  wrapper.innerHTML = buildHTML();
  document.body.appendChild(wrapper.firstElementChild);

  // Wire events
  wireEvents();

  // Keyboard
  _keydownHandler = handleKeydown;
  document.addEventListener("keydown", _keydownHandler);

  // Focus close button
  setTimeout(() => {
    const closeBtn = document.getElementById("qcm-close-btn");
    if (closeBtn) closeBtn.focus();
  }, 50);

  // Prevent body scroll
  document.body.style.overflow = "hidden";
}

function closeModal() {
  const overlay = document.getElementById("qcm-overlay");
  if (overlay) overlay.remove();
  if (_keydownHandler) {
    document.removeEventListener("keydown", _keydownHandler);
    _keydownHandler = null;
  }
  document.body.style.overflow = "";
}

// ─────────────────────────────────────────────────────────────────────────────
// EVENT WIRING
// ─────────────────────────────────────────────────────────────────────────────

function wireEvents() {
  const overlay = document.getElementById("qcm-overlay");
  if (!overlay) return;

  // Close on backdrop click
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeModal();
  });

  // Close button
  const closeBtn = document.getElementById("qcm-close-btn");
  if (closeBtn) closeBtn.addEventListener("click", closeModal);

  // Expand/collapse button
  const expandBtn = document.getElementById("qcm-expand-btn");
  if (expandBtn)
    expandBtn.addEventListener("click", () => {
      const ov = document.getElementById("qcm-overlay");
      if (!ov) return;
      const expanded = ov.classList.toggle("qcm--expanded");
      expandBtn.setAttribute(
        "aria-label",
        expanded ? "Collapse calculator" : "Expand calculator",
      );
      expandBtn.title = expanded ? "Collapse" : "Expand";
    });

  // Sub-service dropdown
  const subSelect = document.getElementById("qcm-sub-select");
  if (subSelect) {
    subSelect.addEventListener("change", () => {
      state.subService = subSelect.value;
      // Academic services all use word count; non-academic never do
      const note = document.getElementById("qcm-wc-note");
      if (note) note.classList.toggle("visible", state.category !== "academic");
    });
  }

  // Word count text input
  const wcInput = document.getElementById("qcm-wc-input");
  if (wcInput) {
    wcInput.addEventListener("input", (e) => syncWordCount(e.target.value));
    wcInput.addEventListener("change", (e) => syncWordCount(e.target.value));
  }

  // Numpad buttons
  overlay.querySelectorAll("[data-qcm-num]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const k = btn.dataset.qcmNum;
      numpadPress(k === "del" || k === "clear" ? k : parseInt(k, 10));
    });
  });

  // Quick presets
  overlay.querySelectorAll("[data-qcm-preset]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const val = parseInt(btn.dataset.qcmPreset, 10);
      state.wordCount = val;
      state._inputBuffer = String(val);
      const input = document.getElementById("qcm-wc-input");
      if (input) input.value = val;
      updateDisplay();
    });
  });

  // Service category
  overlay.querySelectorAll("[data-qcm-cat]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.category = btn.dataset.qcmCat;
      // Reset sub-service to first option in the new category
      const subs = SUB_SERVICES[state.category] || [];
      state.subService = subs[0]?.id || "";
      // Rebuild sub-service dropdown
      const subSel = document.getElementById("qcm-sub-select");
      if (subSel) {
        subSel.innerHTML = subs
          .map((s) => `<option value="${s.id}">${s.label}</option>`)
          .join("");
      }
      updateButtonStates();
      updateDisplay();
      // Show/hide flat-rate note
      const note = document.getElementById("qcm-wc-note");
      if (note) note.classList.toggle("visible", state.category !== "academic");
      // Update Start Order link to point to the correct category services page
      const orderBtn = document.getElementById("qcm-order-btn");
      if (orderBtn) orderBtn.href = getServicePageUrl(state.category);
    });
  });

  // Academic tier
  overlay.querySelectorAll("[data-qcm-tier]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.academicTier = btn.dataset.qcmTier;
      updateButtonStates();
      updateDisplay();
    });
  });

  // Education level
  overlay.querySelectorAll("[data-qcm-level]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.educationLevel = btn.dataset.qcmLevel;
      updateButtonStates();
      updateDisplay();
    });
  });

  // Complexity
  overlay.querySelectorAll("[data-qcm-complexity]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.complexity = btn.dataset.qcmComplexity;
      updateButtonStates();
      updateDisplay();
    });
  });

  // Delivery speed
  overlay.querySelectorAll("[data-qcm-delivery]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.delivery = btn.dataset.qcmDelivery;
      updateButtonStates();
      updateDisplay();
    });
  });

  // Package
  overlay.querySelectorAll("[data-qcm-pkg]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.package = btn.dataset.qcmPkg;
      updateButtonStates();
      updateDisplay();
    });
  });

  // Add-ons
  overlay.querySelectorAll("[data-qcm-addon]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const addon = btn.dataset.qcmAddon;
      if (addon === "proofreading")
        state.includeProofreading = !state.includeProofreading;
      if (addon === "editing") state.includeEditing = !state.includeEditing;
      updateButtonStates();
      updateDisplay();
    });
  });

  // Start Order button — opens the static site service page for the selected category.
  // Does NOT go to checkout; user picks the specific service there and adds to basket.
  const orderBtn = document.getElementById("qcm-order-btn");
  if (orderBtn) {
    orderBtn.href = getServicePageUrl(state.category);
    orderBtn.addEventListener("click", () => {
      orderBtn.href = getServicePageUrl(state.category);
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────────────────────

export const QuoteCalculator = {
  open: openModal,
  close: closeModal,
  setCategory(cat) {
    if (SERVICE_CATEGORIES[cat]) {
      state.category = cat;
    }
  },
};

window.QuoteCalculator = QuoteCalculator;
export default QuoteCalculator;
